import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/anime';

const navItems = [
  { label: '首页', to: '/' },
  { label: '分类', to: '/category/少年' },
  { label: '搜索', to: '/search' },
  { label: '关于', to: '/about' },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const isHome = location.pathname === '/';
  const transparent = isHome && !scrolled && !mobileOpen;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCategoryOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setCategoryOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : '/search');
    setMobileOpen(false);
  };

  const solidClass = 'bg-background-50/95 border-background-200';
  const transparentClass = 'bg-transparent border-transparent';
  const textClass = transparent ? 'text-background-50' : 'text-foreground-900';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 border-b backdrop-blur ${
        transparent ? transparentClass : solidClass
      }`}
    >
      <div className="w-full px-4 md:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 cursor-pointer shrink-0">
          <span
            className={`w-9 h-9 flex items-center justify-center rounded-lg ${
              transparent ? 'bg-background-50/20 text-background-50' : 'bg-primary-500 text-background-50 dark:text-foreground-950'
            }`}
          >
            <i className="ri-play-circle-fill text-xl"></i>
          </span>
          <span className={`font-heading font-bold text-lg whitespace-nowrap ${textClass}`}>
            动漫在线观看
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((item) =>
            item.label === '分类' ? (
              <div key={item.label} className="relative" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={() => setCategoryOpen((v) => !v)}
                  className={`px-3 py-2 rounded-md text-sm font-medium flex items-center gap-1 whitespace-nowrap cursor-pointer transition-colors hover:text-accent-500 ${textClass}`}
                >
                  分类
                  <i className={`ri-arrow-down-s-line transition-transform ${categoryOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {categoryOpen && (
                  <div className="absolute left-0 top-full mt-2 w-64 p-2 rounded-lg bg-background-50 border border-background-200 shadow-lg grid grid-cols-2 gap-1 animate-fade-up">
                    {categories.map((category) => (
                      <Link
                        key={category.slug}
                        to={`/category/${encodeURIComponent(category.slug)}`}
                        className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-800 hover:bg-primary-50 hover:text-primary-700 cursor-pointer"
                      >
                        <i className={`${category.icon} text-primary-500`}></i>
                        {category.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={item.label}
                to={item.to}
                className={`px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer transition-colors hover:text-accent-500 ${textClass} ${
                  location.pathname === item.to ? 'text-accent-500' : ''
                }`}
              >
                {item.label}
              </Link>
            ),
          )}
        </nav>

        <form onSubmit={handleSearch} className="hidden md:flex items-center gap-2">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 w-4 h-4 flex items-center justify-center">
              <i className="ri-search-line text-sm"></i>
            </span>
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索动漫"
              className="w-40 lg:w-56 pl-9 pr-3 py-2 rounded-md text-sm bg-background-100 border border-background-300 text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300"
            />
          </div>
        </form>

        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          className={`md:hidden w-10 h-10 flex items-center justify-center rounded-md cursor-pointer ${textClass}`}
          aria-label="打开菜单"
        >
          <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 px-4 py-4 space-y-3 animate-fade-up">
          <form onSubmit={handleSearch} className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 w-4 h-4 flex items-center justify-center">
              <i className="ri-search-line text-sm"></i>
            </span>
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索动漫"
              className="w-full pl-9 pr-3 py-2.5 rounded-md text-sm bg-background-100 border border-background-300 text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400"
            />
          </form>
          <div className="grid grid-cols-2 gap-2">
            {navItems.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className="px-3 py-2.5 rounded-md text-sm font-medium bg-background-100 text-foreground-800 hover:bg-primary-50 hover:text-primary-700 cursor-pointer"
              >
                {item.label}
              </Link>
            ))}
          </div>
          <div className="pt-1">
            <p className="text-xs text-foreground-500 mb-2">动漫分类</p>
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <Link
                  key={category.slug}
                  to={`/category/${encodeURIComponent(category.slug)}`}
                  className="px-3 py-1.5 rounded-full text-xs bg-secondary-100 text-secondary-900 cursor-pointer"
                >
                  {category.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}